import * as React from 'react';
import { Auth } from 'aws-amplify';

const CognitoError = {
    CodeMismatch: 'CodeMismatchException',
    NotAuthorized: 'NotAuthorizedException',
} as const;

interface Options {
    confirmCode: (code: string) => Promise<unknown>;
    onCancel: () => void;
    onExpired: () => void;
    wrongCodeError: string;
    defaultError: string;
}

export function useMfaCodeForm({
    confirmCode,
    onCancel,
    onExpired,
    wrongCodeError,
    defaultError,
}: Options) {
    const [code, setCode] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState<string | undefined>();
    const isSubmitting = React.useRef(false);

    const onSubmit = async () => {
        if (isSubmitting.current || code.length !== 6) return;
        isSubmitting.current = true;
        setIsLoading(true);
        setError(undefined);
        try {
            await confirmCode(code);
            window.location.reload();
        } catch (verificationError) {
            const errorCode = (verificationError as { code?: string }).code;
            if (errorCode === CognitoError.CodeMismatch) setError(wrongCodeError);
            else if (errorCode === CognitoError.NotAuthorized) {
                try {
                    await Auth.signOut();
                } catch {
                } finally {
                    isSubmitting.current = false;
                    setIsLoading(false);
                    onExpired();
                }
                return;
            } else setError(defaultError);
            isSubmitting.current = false;
            setIsLoading(false);
        }
    };

    const cancel = async () => {
        if (isSubmitting.current) return;
        try {
            await Auth.signOut();
        } catch {
        } finally {
            onCancel();
        }
    };

    return { code, onCodeChange: setCode, isLoading, error, onSubmit, onCancel: cancel };
}
